import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, faIdCard, faUpload, faCheckCircle, faExclamationTriangle,
  faClock, faTimesCircle, faArrowLeft, faArrowRight, faRedo,
  faShieldAlt, faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../../i18n/LanguageContext";
import "./kyc-verification.css";

// ── Main Component ────────────────────────────────────────────────────────────
function KycVerification() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const tr = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const interpolate = (text, vars = {}) =>
    Object.entries(vars).reduce(
      (acc, [k, v]) => acc.replace(new RegExp(`{{${k}}}`, "g"), String(v)),
      text
    );

  const STEPS = [
    tr("kyc.stepPersonal", "Personal Info"),
    tr("kyc.stepIdentity", "Identity Document"),
    tr("kyc.stepUpload", "Upload Documents"),
    tr("kyc.stepReview", "Review & Submit"),
  ];

  const localizedDocumentTypes = [
    { value: "citizenship", label: tr("kyc.citizenship", "Citizenship Card") },
    { value: "passport", label: tr("kyc.passport", "Passport") },
    { value: "driving_license", label: tr("kyc.drivingLicense", "Driving Licence") },
    { value: "voter_id", label: tr("kyc.voterId", "Voter ID") },
  ];

  const [loading, setLoading]           = useState(true);
  const [kycStatus, setKycStatus]       = useState("not_submitted");  // from server
  const [latestSubmission, setLatest]   = useState(null);
  const [mismatchDetails, setMismatchDetails] = useState([]);
  const [ocrExtraction, setOcrExtraction] = useState(null);
  const [step, setStep]                 = useState(0);
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [errors, setErrors]             = useState({});
  const [serverError, setServerError]   = useState("");

  // ── Form state ──────────────────────────────────────────────────────────────
  const [personal, setPersonal] = useState({
    full_name:         "",
    date_of_birth:     "",
    nationality:       "Nepali",
    permanent_address: "",
    current_address:   "",
  });

  const [identity, setIdentity] = useState({
    document_type:   "citizenship",
    document_number: "",
    owner_is_agent:      false,
    pan_number:          "",
    bank_account_number: "",
    bank_name:           "",
  });

  const [files, setFiles]       = useState({
    document_front: null,
    document_back: null,
    selfie: null,
    ownership_proof: null,
    property_tax_ward_proof: null,
    authorization_letter: null,
  });
  const [previews, setPreviews] = useState({
    document_front: null,
    document_back: null,
    selfie: null,
    ownership_proof: null,
    property_tax_ward_proof: null,
    authorization_letter: null,
  });

  const userRole = localStorage.getItem("role") || "renter";

  // ── Load existing KYC status ────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    try {
      const res = await fetch("/api/kyc/status", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setKycStatus(data.kycStatus || "not_submitted");
        setLatest(data.latestSubmission || null);
        setMismatchDetails(data.mismatchDetails || []);
        setOcrExtraction(data.ocrExtraction || null);
      }
    } catch (e) {
      console.error("Failed to load KYC status", e);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!submitted || kycStatus !== "pending") return undefined;

    const intervalId = window.setInterval(() => {
      fetchStatus();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [submitted, kycStatus, fetchStatus]);

  // ── File handling ───────────────────────────────────────────────────────────
  const handleFile = (field, file) => {
    if (!file) return;
    setFiles(prev => ({ ...prev, [field]: file }));
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = e => setPreviews(prev => ({ ...prev, [field]: e.target.result }));
      reader.readAsDataURL(file);
    } else {
      setPreviews(prev => ({ ...prev, [field]: null }));
    }
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateStep = (s) => {
    const errs = {};
    if (s === 0) {
      if (!personal.full_name.trim())         errs.full_name         = tr("kyc.fullNameRequired", "Full name is required.");
      if (!personal.date_of_birth)            errs.date_of_birth     = tr("kyc.dateOfBirthRequired", "Date of birth is required.");
      if (!personal.permanent_address.trim()) errs.permanent_address = tr("kyc.permanentAddressRequired", "Permanent address is required.");
      if (!personal.current_address.trim())   errs.current_address   = tr("kyc.currentAddressRequired", "Current address is required.");
    }
    if (s === 1) {
      if (!identity.document_number.trim()) errs.document_number = tr("kyc.documentNumberRequired", "Document number is required.");
    }
    if (s === 2) {
      if (!files.document_front) errs.document_front = tr("kyc.documentFrontRequired", "Front side of document is required.");
      if (!files.selfie)         errs.selfie         = tr("kyc.selfieRequired", "Selfie is required.");
      if (userRole === "owner" && !files.ownership_proof) {
        errs.ownership_proof = tr("kyc.ownershipProofRequired", "Land ownership proof is required.");
      }
      if (userRole === "owner" && !files.property_tax_ward_proof) {
        errs.property_tax_ward_proof = tr("kyc.taxWardProofRequired", "Property tax or ward evidence is required.");
      }
      if (userRole === "owner" && identity.owner_is_agent && !files.authorization_letter) {
        errs.authorization_letter = tr("kyc.authorizationLetterRequired", "Authorization letter is required for agents.");
      }
    }
    return errs;
  };

  const goNext = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setErrors({});
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const errs = validateStep(2);
    if (Object.keys(errs).length) { setErrors(errs); setStep(2); return; }

    setSubmitting(true);
    setServerError("");

    const token = localStorage.getItem("token");
    const formData = new FormData();

    Object.entries(personal).forEach(([k, v]) => formData.append(k, v));
    Object.entries(identity).forEach(([k, v]) => formData.append(k, v));
    if (files.document_front) formData.append("document_front", files.document_front);
    if (files.document_back)  formData.append("document_back",  files.document_back);
    if (files.selfie)         formData.append("selfie",         files.selfie);
    if (files.ownership_proof) formData.append("ownership_proof", files.ownership_proof);
    if (files.property_tax_ward_proof) formData.append("property_tax_ward_proof", files.property_tax_ward_proof);
    if (files.authorization_letter) formData.append("authorization_letter", files.authorization_letter);

    try {
      const res = await fetch("/api/kyc/submit", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        setKycStatus("pending");
        fetchStatus();
      } else {
        setServerError(data.message || tr("kyc.submitFailed", "Submission failed. Please try again."));
      }
    } catch (e) {
      setServerError(tr("kyc.networkError", "Network error. Please check your connection and try again."));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Status screens ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="kyc-page">
        <div className="kyc-loading">{tr("kyc.loading", "Loading your KYC status…")}</div>
      </div>
    );
  }

  if (kycStatus === "pending") {
    return (
      <div className="kyc-page">
        <div className="kyc-container">
          <div className="kyc-card kyc-success">
            <div className="kyc-success-icon"><FontAwesomeIcon icon={faClock} /></div>
            <h2>{tr("kyc.successTitle", "KYC Submitted — Under Review")}</h2>
            <p>
              {tr("kyc.pendingMsg", "Your identity documents have been submitted. Our team will review them within 7–14 days. You'll be able to use the platform fully once verified.")}
            </p>
            <button className="kyc-btn kyc-btn-primary" onClick={() => navigate(userRole === "owner" ? "/owner-dashboard" : "/renter-dashboard")}>
              {tr("kyc.goToDashboard", "Go to Dashboard")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (kycStatus === "approved") {
    return (
      <div className="kyc-page">
        <div className="kyc-container">
          <div className="kyc-card kyc-success">
            <div className="kyc-success-icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
            <h2>{tr("kyc.approvedTitle", "Identity Verified")}</h2>
            <p>{tr("kyc.approvedMsg", "Your KYC verification is complete. You have full access to all GharBhada features.")}</p>
            <button className="kyc-btn kyc-btn-primary" onClick={() => navigate(userRole === "owner" ? "/owner-dashboard" : "/renter-dashboard")}>
              {tr("kyc.goToDashboard", "Go to Dashboard")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (kycStatus === "mismatch_flagged") {
    return (
      <div className="kyc-page">
        <div className="kyc-container">
          <div className="kyc-card kyc-success" style={{ borderLeft: "4px solid #d97706" }}>
            <div className="kyc-success-icon" style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <h2>{tr("kyc.mismatchTitle", "KYC needs manual review")}</h2>
            <p>
              {tr(
                "kyc.mismatchMsg",
                "We detected differences between your submitted details and the OCR result from your citizenship document. An admin will review it manually before verification can continue."
              )}
            </p>

            {ocrExtraction && (
              <div className="kyc-status-banner rejected" style={{ marginTop: 18, textAlign: "left" }}>
                <div>
                  <div className="banner-title" style={{ marginBottom: 8 }}>
                    {tr("kyc.ocrSummary", "OCR Summary")}
                  </div>
                  <div style={{ fontSize: ".9rem", lineHeight: 1.6 }}>
                    <div><strong>{tr("kyc.ocrName", "Name")}:</strong> {ocrExtraction.extracted_name || "—"}</div>
                    <div><strong>{tr("kyc.ocrCitizen", "Citizenship No.")}:</strong> {ocrExtraction.extracted_citizenship_number || "—"}</div>
                    <div><strong>{tr("kyc.ocrDob", "Date of Birth")}:</strong> {ocrExtraction.extracted_date_of_birth || "—"}</div>
                    <div><strong>{tr("kyc.ocrAddress", "Address")}:</strong> {ocrExtraction.extracted_address || "—"}</div>
                  </div>
                </div>
              </div>
            )}

            {Array.isArray(mismatchDetails) && mismatchDetails.length > 0 && (
              <div style={{ marginTop: 18, textAlign: "left" }}>
                <div className="banner-title" style={{ marginBottom: 10 }}>
                  {tr("kyc.mismatchFields", "Fields flagged for review")}
                </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {mismatchDetails.map((mismatch) => (
                    <div key={mismatch.id} style={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 12, padding: 12, background: "rgba(255,255,255,0.65)" }}>
                      <div style={{ fontWeight: 700, textTransform: "capitalize", marginBottom: 6 }}>
                        {String(mismatch.field_name || "field").replace(/_/g, " ")}
                      </div>
                      <div><strong>{tr("kyc.userValue", "Your value")}:</strong> {mismatch.user_submitted_value || "—"}</div>
                      <div><strong>{tr("kyc.ocrValue", "OCR value")}:</strong> {mismatch.ocr_extracted_value || "—"}</div>
                      <div><strong>{tr("kyc.reviewState", "Review state")}:</strong> {mismatch.review_status || "pending"}</div>
                      {mismatch.reviewer_notes && <div><strong>{tr("kyc.reviewerNotes", "Reviewer notes")}:</strong> {mismatch.reviewer_notes}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 18, fontSize: ".95rem", lineHeight: 1.6 }}>
              {tr(
                "kyc.mismatchWait",
                "You do not need to resubmit yet. If the reviewer marks this submission for resubmission, the form will reopen with the rejection reason shown above."
              )}
            </div>

            <button className="kyc-btn kyc-btn-primary" style={{ marginTop: 18 }} onClick={() => navigate(userRole === "owner" ? "/owner-dashboard" : "/renter-dashboard")}>
              {tr("kyc.goToDashboard", "Go to Dashboard")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────────
  return (
    <div className="kyc-page">
      <div className="kyc-container">

        {/* Header */}
        <div className="kyc-header">
          <h1><FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: 10, color: "#4f46e5" }} />
            {tr("kyc.title", "Identity Verification (KYC)")}
          </h1>
          <p>
            {interpolate(tr("kyc.forRole", "Verify your identity to unlock full access and build trust with {{role}}."), {
              role: userRole === "owner" ? tr("kyc.roleOwnerPlural", "renters") : tr("kyc.roleRenterPlural", "owners"),
            })}
          </p>
        </div>

        {/* Rejection / resubmit banner */}
        {(kycStatus === "rejected" || kycStatus === "resubmit" || kycStatus === "mismatch_flagged") && latestSubmission && (
          <div className={`kyc-status-banner ${kycStatus}`}>
            <FontAwesomeIcon icon={kycStatus === "resubmit" ? faRedo : faTimesCircle} />
            <div>
              <div className="banner-title">
                {kycStatus === "resubmit" ? tr("kyc.resubmitTitle", "Resubmission Required") : tr("kyc.rejectedTitle", "KYC Rejected")}
              </div>
              <div>{latestSubmission.rejection_reason || tr("kyc.rejectedFallback", "Your submission did not meet the requirements.")}</div>
              {latestSubmission.mismatch_reason && (
                <div style={{ marginTop: 4 }}>{latestSubmission.mismatch_reason}</div>
              )}
              {Array.isArray(mismatchDetails) && mismatchDetails.length > 0 && (
                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  {mismatchDetails.map((mismatch) => (
                    <div key={mismatch.id} style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,.55)" }}>
                      <strong style={{ textTransform: "capitalize" }}>{String(mismatch.field_name || "field").replace(/_/g, " ")}</strong>
                      <div style={{ marginTop: 4 }}>
                        <strong>{tr("kyc.userValue", "Your value")}:</strong> {mismatch.user_submitted_value || "—"}
                      </div>
                      <div>
                        <strong>{tr("kyc.ocrValue", "OCR value")}:</strong> {mismatch.ocr_extracted_value || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {kycStatus === "resubmit" && <div style={{ marginTop: 4 }}>{tr("kyc.resubmitHelp", "Please correct the issues and resubmit below.")}</div>}
            </div>
          </div>
        )}

        {/* Step indicator */}
        <div className="kyc-steps">
          {STEPS.map((label, i) => (
            <div
              key={i}
              className={`kyc-step ${i === step ? "active" : i < step ? "completed" : ""}`}
            >
              <span className="kyc-step-num">{i < step ? "✓" : i + 1}</span>
              {label}
            </div>
          ))}
        </div>

        {/* ── Step 0: Personal Information ─────────────────────────────────── */}
        {step === 0 && (
          <div className="kyc-card">
            <div className="kyc-card-title">
              <FontAwesomeIcon icon={faUser} /> {tr("kyc.stepPersonal", "Personal Info")}
            </div>
            <div className="kyc-grid-2">
              <div className="kyc-field">
                <label>{tr("kyc.fullName", "Full Name")} <span className="required">*</span></label>
                <input
                  type="text"
                  placeholder={tr("kyc.asOnId", "As it appears on your ID")}
                  value={personal.full_name}
                  onChange={e => setPersonal(p => ({ ...p, full_name: e.target.value }))}
                />
                {errors.full_name && <span className="field-error">{errors.full_name}</span>}
              </div>

              <div className="kyc-field">
                <label>{tr("kyc.dateOfBirth", "Date of Birth")} <span className="required">*</span></label>
                <input
                  type="date"
                  value={personal.date_of_birth}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]}
                  onChange={e => setPersonal(p => ({ ...p, date_of_birth: e.target.value }))}
                />
                {errors.date_of_birth && <span className="field-error">{errors.date_of_birth}</span>}
              </div>

              <div className="kyc-field">
                <label>{tr("kyc.nationality", "Nationality")}</label>
                <input
                  type="text"
                  value={personal.nationality}
                  onChange={e => setPersonal(p => ({ ...p, nationality: e.target.value }))}
                />
              </div>

              <div className="kyc-field full-width">
                <label>{tr("kyc.permanentAddress", "Permanent Address")} <span className="required">*</span></label>
                <textarea
                  placeholder={tr("kyc.addressPlaceholder", "Ward No., Municipality / VDC, District, Province")}
                  value={personal.permanent_address}
                  onChange={e => setPersonal(p => ({ ...p, permanent_address: e.target.value }))}
                />
                {errors.permanent_address && <span className="field-error">{errors.permanent_address}</span>}
              </div>

              <div className="kyc-field full-width">
                <label>{tr("kyc.currentAddress", "Current Address")} <span className="required">*</span></label>
                <textarea
                  placeholder={tr("kyc.currentAddressPlaceholder", "Enter your current residential address")}
                  value={personal.current_address}
                  onChange={e => setPersonal(p => ({ ...p, current_address: e.target.value }))}
                />
                {errors.current_address && <span className="field-error">{errors.current_address}</span>}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 1: Identity Document ─────────────────────────────────────── */}
        {step === 1 && (
          <div className="kyc-card">
            <div className="kyc-card-title">
              <FontAwesomeIcon icon={faIdCard} /> {tr("kyc.stepIdentity", "Identity Document")}
            </div>
            <div className="kyc-grid-2">
              <div className="kyc-field">
                <label>{tr("kyc.documentType", "Document Type")} <span className="required">*</span></label>
                <select
                  value={identity.document_type}
                  onChange={e => setIdentity(p => ({ ...p, document_type: e.target.value }))}
                >
                  {localizedDocumentTypes.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>

              <div className="kyc-field">
                <label>{tr("kyc.documentNumber", "Document Number")} <span className="required">*</span></label>
                <input
                  type="text"
                  placeholder={tr("kyc.documentNumberPlaceholder", "e.g. 12-01-76-12345")}
                  value={identity.document_number}
                  onChange={e => setIdentity(p => ({ ...p, document_number: e.target.value }))}
                />
                {errors.document_number && <span className="field-error">{errors.document_number}</span>}
              </div>

              {/* Owner-specific fields */}
              {userRole === "owner" && (
                <>
                  <div className="kyc-field full-width" style={{ gridColumn: "1 / -1", marginTop: 8 }}>
                    <div className="kyc-card-title" style={{ fontSize: "0.95rem", marginBottom: 12 }}>
                      <FontAwesomeIcon icon={faBuilding} /> {tr("kyc.ownerDetails", "Owner Details")} <span className="kyc-hint" style={{ fontWeight: 400 }}>{tr("kyc.ownerDetailsRequired", "(required for landlord verification)")}</span>
                    </div>
                  </div>
                  <div className="kyc-field full-width">
                    <label>
                      <input
                        type="checkbox"
                        checked={identity.owner_is_agent}
                        onChange={e => setIdentity(p => ({ ...p, owner_is_agent: e.target.checked }))}
                        style={{ marginRight: 8 }}
                      />
                      {tr("kyc.ownerIsAgent", "I am listing as an authorized agent, not the direct owner")}
                    </label>
                  </div>
                  <div className="kyc-field">
                    <label>{tr("kyc.panNumber", "PAN Number")}</label>
                    <input
                      type="text"
                      placeholder={tr("kyc.panPlaceholder", "e.g. 123456789")}
                      value={identity.pan_number}
                      onChange={e => setIdentity(p => ({ ...p, pan_number: e.target.value }))}
                    />
                  </div>
                  <div className="kyc-field">
                    <label>{tr("kyc.bankName", "Bank Name")}</label>
                    <input
                      type="text"
                      placeholder={tr("kyc.bankNamePlaceholder", "e.g. Nepal Bank Limited")}
                      value={identity.bank_name}
                      onChange={e => setIdentity(p => ({ ...p, bank_name: e.target.value }))}
                    />
                  </div>
                  <div className="kyc-field full-width">
                    <label>{tr("kyc.bankAccount", "Bank Account Number")}</label>
                    <input
                      type="text"
                      placeholder={tr("kyc.bankAccountPlaceholder", "Your bank account number")}
                      value={identity.bank_account_number}
                      onChange={e => setIdentity(p => ({ ...p, bank_account_number: e.target.value }))}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Step 2: Upload Documents ──────────────────────────────────────── */}
        {step === 2 && (
          <div className="kyc-card">
            <div className="kyc-card-title">
              <FontAwesomeIcon icon={faUpload} /> {tr("kyc.uploadTitle", "Upload Documents")}
            </div>
            <div className="kyc-upload-grid">
              <UploadZone
                label={tr("kyc.docFront", "Front Side of Document")}
                required
                field="document_front"
                files={files}
                previews={previews}
                errors={errors}
                hint={interpolate(tr("kyc.uploadFrontHint", "Upload the front side of your {{document}}"), {
                  document: localizedDocumentTypes.find(d => d.value === identity.document_type)?.label || tr("kyc.documentType", "document"),
                })}
                onFile={handleFile}
                clickToUpload={tr("kyc.clickToUpload", "Click to upload")}
                dragDrop={tr("kyc.dragDrop", "or drag & drop")}
                uploadedText={tr("kyc.uploaded", "Uploaded")}
              />
              <UploadZone
                label={tr("kyc.docBack", "Back Side of Document")}
                field="document_back"
                files={files}
                previews={previews}
                errors={errors}
                hint={tr("kyc.uploadBackHint", "Optional — include if applicable")}
                onFile={handleFile}
                clickToUpload={tr("kyc.clickToUpload", "Click to upload")}
                dragDrop={tr("kyc.dragDrop", "or drag & drop")}
                uploadedText={tr("kyc.uploaded", "Uploaded")}
              />
              <UploadZone
                label={tr("kyc.selfie", "Selfie / Live Photo")}
                required
                field="selfie"
                files={files}
                previews={previews}
                errors={errors}
                hint={tr("kyc.uploadSelfieHint", "Hold your ID next to your face. Clear, well-lit photo.")}
                onFile={handleFile}
                clickToUpload={tr("kyc.clickToUpload", "Click to upload")}
                dragDrop={tr("kyc.dragDrop", "or drag & drop")}
                uploadedText={tr("kyc.uploaded", "Uploaded")}
              />
              {userRole === "owner" && (
                <>
                  <UploadZone
                    label={tr("kyc.ownershipProof", "Land Ownership Proof")}
                    required
                    field="ownership_proof"
                    files={files}
                    previews={previews}
                    errors={errors}
                    hint={tr("kyc.ownershipProofHint", "Upload land ownership certificate or equivalent ownership document.")}
                    onFile={handleFile}
                    clickToUpload={tr("kyc.clickToUpload", "Click to upload")}
                    dragDrop={tr("kyc.dragDrop", "or drag & drop")}
                    uploadedText={tr("kyc.uploaded", "Uploaded")}
                  />
                  <UploadZone
                    label={tr("kyc.taxWardProof", "Property Tax / Ward Evidence")}
                    required
                    field="property_tax_ward_proof"
                    files={files}
                    previews={previews}
                    errors={errors}
                    hint={tr("kyc.taxWardProofHint", "Upload latest property tax receipt or ward verification evidence.")}
                    onFile={handleFile}
                    clickToUpload={tr("kyc.clickToUpload", "Click to upload")}
                    dragDrop={tr("kyc.dragDrop", "or drag & drop")}
                    uploadedText={tr("kyc.uploaded", "Uploaded")}
                  />
                  {identity.owner_is_agent && (
                    <UploadZone
                      label={tr("kyc.authorizationLetter", "Authorization Letter")}
                      required
                      field="authorization_letter"
                      files={files}
                      previews={previews}
                      errors={errors}
                      hint={tr("kyc.authorizationLetterHint", "Upload signed authorization letter from the owner.")}
                      onFile={handleFile}
                      clickToUpload={tr("kyc.clickToUpload", "Click to upload")}
                      dragDrop={tr("kyc.dragDrop", "or drag & drop")}
                      uploadedText={tr("kyc.uploaded", "Uploaded")}
                    />
                  )}
                </>
              )}
            </div>
            <p className="kyc-hint" style={{ marginTop: 16 }}>
              {tr("kyc.acceptedFormats", "Accepted formats: JPEG, PNG, WEBP, PDF. Max 10 MB per file. Documents must be clear and fully visible.")}
            </p>
          </div>
        )}

        {/* ── Step 3: Review & Submit ───────────────────────────────────────── */}
        {step === 3 && (
          <div className="kyc-card">
            <div className="kyc-card-title">
              <FontAwesomeIcon icon={faCheckCircle} /> {tr("kyc.reviewTitle", "Review & Submit")}
            </div>
            <ReviewRow label={tr("kyc.reviewFullName", "Full Name")}         value={personal.full_name} />
            <ReviewRow label={tr("kyc.reviewDob", "Date of Birth")}          value={personal.date_of_birth} />
            <ReviewRow label={tr("kyc.reviewNationality", "Nationality")}    value={personal.nationality} />
            <ReviewRow label={tr("kyc.reviewPermanentAddress", "Permanent Address")} value={personal.permanent_address} />
            {personal.current_address && <ReviewRow label={tr("kyc.reviewCurrentAddress", "Current Address")} value={personal.current_address} />}
            <ReviewRow label={tr("kyc.reviewDocType", "Document Type")}      value={localizedDocumentTypes.find(d => d.value === identity.document_type)?.label} />
            <ReviewRow label={tr("kyc.reviewDocNumber", "Document Number")}  value={identity.document_number} />
            {userRole === "owner" && identity.pan_number       && <ReviewRow label={tr("kyc.reviewPan", "PAN Number")} value={identity.pan_number} />}
            {userRole === "owner" && identity.bank_name        && <ReviewRow label={tr("kyc.reviewBank", "Bank Name")} value={identity.bank_name} />}
            {userRole === "owner" && identity.bank_account_number && <ReviewRow label={tr("kyc.reviewAccount", "Account No.")} value={identity.bank_account_number} />}
            {userRole === "owner" && <ReviewRow label={tr("kyc.reviewOwnerIsAgent", "Submitting As")} value={identity.owner_is_agent ? tr("kyc.agent", "Authorized Agent") : tr("kyc.directOwner", "Direct Owner")} />}
            <ReviewRow label={tr("kyc.reviewDocFront", "Document Front")}     value={files.document_front?.name || tr("kyc.dash", "—")} />
            {files.document_back && <ReviewRow label={tr("kyc.reviewDocBack", "Document Back")} value={files.document_back?.name} />}
            <ReviewRow label={tr("kyc.reviewSelfie", "Selfie")}               value={files.selfie?.name || tr("kyc.dash", "—")} />
            {userRole === "owner" && <ReviewRow label={tr("kyc.reviewOwnershipProof", "Ownership Proof")} value={files.ownership_proof?.name || tr("kyc.dash", "—")} />}
            {userRole === "owner" && <ReviewRow label={tr("kyc.reviewTaxWardProof", "Tax/Ward Proof")} value={files.property_tax_ward_proof?.name || tr("kyc.dash", "—")} />}
            {userRole === "owner" && identity.owner_is_agent && <ReviewRow label={tr("kyc.reviewAuthorizationLetter", "Authorization Letter")} value={files.authorization_letter?.name || tr("kyc.dash", "—")} />}

            <p className="kyc-hint" style={{ marginTop: 18, lineHeight: 1.6 }}>
              {tr("kyc.submitConfirm", "By submitting, you confirm that all information and documents are genuine and belong to you. Providing false documents is a violation of our Terms of Service and may be reported to relevant authorities.")}
            </p>

            {serverError && (
              <div className="kyc-status-banner rejected" style={{ marginTop: 16 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <div>{serverError}</div>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="kyc-nav">
          <button
            className="kyc-btn kyc-btn-secondary"
            onClick={step === 0 ? () => navigate(-1) : goBack}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 6 }} />
            {step === 0 ? tr("kyc.cancel", "Cancel") : tr("kyc.back", "Back")}
          </button>

          {step < STEPS.length - 1 ? (
            <button className="kyc-btn kyc-btn-primary" onClick={goNext}>
              {tr("kyc.continue", "Continue")} <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 6 }} />
            </button>
          ) : (
            <button
              className="kyc-btn kyc-btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? tr("kyc.submitting", "Submitting…") : tr("kyc.submit", "Submit KYC")}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Upload Zone sub-component ─────────────────────────────────────────────────
function UploadZone({ label, required, field, files, previews, errors, hint, onFile, clickToUpload, dragDrop, uploadedText }) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div className="kyc-field">
      <label>
        {label} {required && <span className="required">*</span>}
      </label>
      <div
        className={`kyc-upload-zone${dragOver ? " drag-over" : ""}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) onFile(field, f); }}
      >
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.pdf"
          onChange={e => { if (e.target.files[0]) onFile(field, e.target.files[0]); }}
        />
        {files[field] ? (
          <div className="file-preview">
            {previews[field] && <img src={previews[field]} alt="preview" />}
            <span>✓ {uploadedText ? `${uploadedText}: ` : ""}{files[field].name}</span>
          </div>
        ) : (
          <>
            <div className="upload-icon"><FontAwesomeIcon icon={faUpload} /></div>
            <div className="upload-label">
              <strong>{clickToUpload || "Click to upload"}</strong> {dragDrop || "or drag & drop"}
            </div>
          </>
        )}
      </div>
      {hint && <span className="kyc-hint">{hint}</span>}
      {errors[field] && <span className="field-error">{errors[field]}</span>}
    </div>
  );
}

// ── Review row ───────────────────────────────────────────────────────────────
function ReviewRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid #f3f4f6", fontSize: "0.88rem" }}>
      <span style={{ width: 180, flexShrink: 0, color: "#6b7280", fontWeight: 600 }}>{label}</span>
      <span style={{ color: "#111827", wordBreak: "break-all" }}>{value || "-"}</span>
    </div>
  );
}

export default KycVerification;
